//--- React
import { useSelector } from "react-redux";
//--- Ant Design
import { Card, Col, Row } from "antd";
import dayjs from "dayjs";
//--- Project
import BATTERY_ICON from "assets/images/icons/BatteryCharging.svg";
import CLOCK_ICON from "assets/images/icons/Clock.svg";
import CLOCKWISE_ICON from "assets/images/icons/ClockClockwise.svg";
import DISTANCE_ICON from "assets/images/icons/distance_location.svg";
import RIDE_END_POINTS from "assets/images/icons/rideEndPoints.svg";
import VehicleImageWidget from "components/Common/Widgets/VehicleImageWidget";
import { UNITS } from "components/Common/Constants";
import TripHighlight from "./TripHighlight";
import SpeedMap from "./SpeedMap";
import BrakeAlalytics from "./BrakeAlalytics";
import EnergyAnalytics from "./EnergyAnalytics";

const Overview = () => {
  const vehicleDetails = useSelector((state) => state.eol?.vehicleDetails);
  const tripSummary = useSelector((state) => state.testRide.tripSummary);

  return (
    <Row gutter={[16, 16]} className="overview">
      <Col sm={24} md={12} lg={8} xl={6} className="mt1">
        <Row className="pd075 border h100" align={"middle"} justify={"center"}>
          <Col span={22} className="mt1">
            <VehicleImageWidget
              vehicleDetails={vehicleDetails}
              showDetails={true}
              showOdometer={false}
            />
          </Col>
        </Row>
      </Col>
      <Col sm={24} md={12} lg={6} xl={8} className="mt1">
        <Row gutter={[16, 16]} wrap={true} className="h100">
          <Col span={24}>
            <Card
              className="border h100"
              title={
                <div className="card-title mt1">
                  <img src={CLOCK_ICON} alt="clock" className="plr05" />
                  {"Time"}
                </div>
              }
            >
              <div className="time-box pd05">
                <div className="ride-start-time-box text-center">
                  <div>
                    <div className="label">Start</div>
                    <div className="value">
                      {tripSummary?.testStartTime ? (
                        dayjs(tripSummary.testStartTime).format("hh:mm A")
                      ) : (
                        <span className="disabled ml025">NA</span>
                      )}
                    </div>
                    <div className="label">
                      {(tripSummary?.testEndTime && dayjs(tripSummary?.testStartTime).date() !== dayjs(tripSummary?.testEndTime).date()) ? (
                        dayjs(tripSummary.testStartTime).format("DD/MM/YYYY")
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="align-center">
                  <img
                    src={RIDE_END_POINTS}
                    alt="ride"
                    className="mr1 ride-start-end-icon"
                    height="80%"
                    width={"100%"}
                  />
                </div>
                <div className="ride-end-time-box text-center">
                  <div>
                    <div className="label">End</div>
                    <div className="value">
                      {tripSummary?.testEndTime ? (
                        dayjs(tripSummary.testEndTime).format("hh:mm A")
                      ) : (
                        <span className="disabled ml025">NA</span>
                      )}
                    </div>
                    <div className="label">
                      {(tripSummary?.testEndTime && dayjs(tripSummary?.testStartTime).date() !== dayjs(tripSummary?.testEndTime).date()) ? (
                        dayjs(tripSummary.testEndTime).format("DD/MM/YYYY")
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </Col>
          <Col span={24}>
            <Card
              className="border h100"
              title={
                <div className="card-title mt1">
                  <img src={DISTANCE_ICON} alt="distance" className="plr05" />
                  {"Distance"}
                </div>
              }
            >
              <div className="flex-around mt1 pd05">
                <div className="flex-center">
                  <div className="text-center">
                    <div className="label">Motor</div>
                    <div className="value">
                      {tripSummary?.totalMotorDistance ? (
                        tripSummary.totalMotorDistance + " " + UNITS?.KM
                      ) : (
                        <span className="disabled ml025">NA</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex-center">
                  <div className="text-center">
                    <div className="label">GPS</div>
                    <div className="value">
                      {tripSummary?.totalGpsDistance ? (
                        tripSummary.totalGpsDistance + " " + UNITS?.KM
                      ) : (
                        <span className="disabled ml025">NA</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </Col>
      <Col sm={24} md={24} lg={10} xl={10} className="mt1">
        <Row gutter={[16, 16]} wrap={true} className="h100">
          <Col span={24}>
            <Card
              className="border h100"
              title={
                <div className="card-title mt1">
                  <img src={CLOCKWISE_ICON} alt="distance" className="plr05" />
                  {"Duration"}
                </div>
              }
            >
              <Row
                align={"center"}
                justify={"space-around"}
                className="time-box pd05"
              >
                <Col span={8} className="text-center pdtb05 border-right">
                  <div className="label">Ride</div>
                  <div className="value">
                    {tripSummary?.tripDuration || (
                      <span className="disabled ml025">NA</span>
                    )}
                  </div>
                </Col>
                <Col span={8} className="text-center pdtb05 border-right">
                  <div>
                    <div className="label">Running</div>
                    <div className="value">
                      {tripSummary?.runningDuration || (
                        <span className="disabled ml025">NA</span>
                      )}
                    </div>
                  </div>
                </Col>
                <Col span={8} className="text-center pdtb05">
                  <div>
                    <div className="label">Stopped</div>
                    <div className="value">
                      {tripSummary?.stopDuration || (
                        <span className="disabled ml025">NA</span>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
          <Col span={24}>
            <Card
              className="border h100"
              title={
                <div className="card-title mt1">
                  <img src={BATTERY_ICON} alt="distance" className="plr05" />
                  {"Charge"}
                </div>
              }
            >
              <Row align={"center"} className="pd05">
                <Col span={8} className="text-center pdtb05 border-right">
                  <div className="label">Starting</div>
                  <div className="value">
                    {tripSummary?.startSoc ? (
                      tripSummary.startSoc + " %"
                    ) : (
                      <span className="disabled ml025">NA</span>
                    )}
                  </div>
                </Col>
                <Col span={8} className="text-center pdtb05 border-right">
                  <div>
                    <div className="label">Ending</div>
                    <div className="value">
                      {tripSummary?.endSoc ? (
                        tripSummary.endSoc + " %"
                      ) : (
                        <span className="disabled ml025">NA</span>
                      )}
                    </div>
                  </div>
                </Col>
                <Col span={8} className="text-center pdtb05">
                  <div>
                    <div className="label">Discharge</div>
                    <div className="value">
                      {tripSummary?.totalDischarge ? (
                        tripSummary.totalDischarge + " %"
                      ) : (
                        <span className="disabled ml025">NA</span>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Col>
      <Col span={24}>
        <EnergyAnalytics />
      </Col>
      <Col span={24}>
        <TripHighlight />
      </Col>
      <Col span={24}>
        <SpeedMap />
      </Col>
      <Col span={24}>
        <BrakeAlalytics />
      </Col>
    </Row>
  );
};

export default Overview;
